import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, finalize } from 'rxjs';

import {
	AlarisButtonComponent,
	AlarisCustomFormComponent,
	AlarisEditPanelService,
	AlarisFadeDirective,
	AlarisFileLoaderComponent,
	AlarisIconDirective,
	AlarisInputComponent,
	AlarisLoadingDirective,
	AlarisMultiOptions2Component,
	AlarisMultiSelect2Component,
	AlarisTextAreaComponent,
	AlarisTooltipDirective,
	CustomValidators,
	EditPanelInputData,
	extractCustomFields,
} from '@campaign-portal/components-library';

import { SenderSubscriptionEnabledStatus, TrafficType } from '@campaign-portal/namespace/common/enums';
import { SenderSubscription } from '@campaign-portal/namespace/entities/sender-id/specs';
import { Channel } from '@campaign-portal/namespace/entities/channels/specs';
import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';

import { ChannelsService } from '@helpers/services/channels.service';
import { CanDeactivateComponent } from '@helpers/shared/can-deactivate/component-deactivate';
import { CanDeactivateGuardService } from '@helpers/shared/can-deactivate/can-deactivate.guard';
import { SendersService } from '../../services/senders.service';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomEntityField } from '@campaign-portal/namespace/entities/custom-forms/specs';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { SendersCustomFieldsService } from '../senders.custom-fields.service';

interface SenderIdControls {
	name: FormControl<string>;
	channels: FormControl<null | Channel[]>;
	comment: FormControl<string>;
	documents: FormControl<FileInfo[]>;
}

export interface WhatsappSettingsControls {
	businessManagerId: FormControl<string>;
	phone: FormControl<string>;
	logo: FormControl<FileInfo | null>;
	description: FormControl<string>;
	address: FormControl<string>;
	email: FormControl<string>;
	website: FormControl<string>;
}

export interface RequestSenderInputData {
	requestCustomFields?: boolean;
	customFields?: CustomEntityField[];
	customFormIds?: Id<exist>[];
}

@Component({
	selector: 'app-sender-request',
	templateUrl: './sender-request.component.html',
	styleUrls: ['./sender-request.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AlarisLoadingDirective,
		AlarisIconDirective,
		TranslateModule,
		AlarisFadeDirective,
		AlarisInputComponent,
		AlarisMultiSelect2Component,
		AlarisMultiOptions2Component,
		AsyncPipe,
		AlarisTextAreaComponent,
		AlarisFileLoaderComponent,
		AlarisButtonComponent,
		ReactiveFormsModule,
		AlarisCustomFormComponent,
		AlarisTooltipDirective,
	],
	providers: [SendersCustomFieldsService],
	standalone: true,
})
export class SenderRequestComponent extends CanDeactivateComponent implements OnInit {
	senderIdForm!: FormGroup<SenderIdControls>;
	whatsappSettingsForm!: FormGroup<WhatsappSettingsControls>;

	requestCustomFields = false;
	customFields: CustomEntityField[] = [];
	customFormIds?: Id<exist>[];
	readonly customForm = new UntypedFormGroup({});

	readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly allowedDeactivation = new BehaviorSubject<boolean>(true);

	constructor(
		@Inject(EditPanelInputData) private readonly inputData: RequestSenderInputData,
		private readonly editPanel: AlarisEditPanelService,
		public readonly sendersService: SendersService,
		public readonly channelService: ChannelsService,
		private readonly customFieldsService: SendersCustomFieldsService,
		private readonly guard: CanDeactivateGuardService
	) {
		super();
		this.addEditPanelGuard(editPanel, this.guard);
		this.requestCustomFields = this.inputData.requestCustomFields ?? false;
		this.customFields = this.inputData.customFields ?? [];
		this.customFormIds = this.inputData.customFormIds;
	}

	get hasWhatsappChannel(): boolean {
		const channels =
			this.senderIdForm.controls.channels.value === null
				? this.channelService.list
				: this.senderIdForm.controls.channels.value;
		return Boolean(channels?.find((ch) => ch.channelType === TrafficType.WHATSAPP));
	}

	close(): void {
		this.editPanel.close();
	}

	ngOnInit(): void {
		this.senderIdForm = new FormGroup({
			name: new FormControl<string>('', { validators: Validators.required, nonNullable: true }),
			channels: new FormControl<null | Channel[]>([], [CustomValidators.requiredArrayOrNull]),
			comment: new FormControl<string>('', { nonNullable: true }),
			documents: new FormControl<FileInfo[]>([], { nonNullable: true }),
		});

		this.whatsappSettingsForm = new FormGroup({
			businessManagerId: new FormControl('', { nonNullable: true }),
			phone: new FormControl('', { nonNullable: true, validators: Validators.required }),
			logo: new FormControl<FileInfo | null>(null),
			description: new FormControl('', { nonNullable: true }),
			address: new FormControl('', { nonNullable: true }),
			email: new FormControl('', { nonNullable: true, validators: Validators.email }),
			website: new FormControl('', { nonNullable: true }),
		});

		if (this.requestCustomFields) {
			this.loading$.next(true);
			this.customFieldsService
				.read()
				.pipe(
					finalize(() => this.loading$.next(false)),
					takeUntilDestroyed(this.destroyRef)
				)
				.subscribe((resp) => {
					const { customFormsFields, requestCustomFormIds } = resp.Data.at(0) ?? {};
					this.customFormIds = requestCustomFormIds;
					this.customFields = extractCustomFields(customFormsFields ?? [], requestCustomFormIds);
				});
		}
	}

	updateLogo(files: (FileInfo | undefined)[]): void {
		this.whatsappSettingsForm.controls.logo.setValue(files.at(0) ?? null);
		this.whatsappSettingsForm.controls.logo.markAsDirty();
	}

	updateDocuments(files: (FileInfo | undefined)[]): void {
		this.senderIdForm.controls.documents.setValue(files.filter(Boolean) as FileInfo[]);
		this.senderIdForm.markAsDirty();
	}

	save(): void {
		const formValue = this.senderIdForm.getRawValue();
		const senderIds: SenderSubscription[] =
			formValue.channels === null
				? this.channelService.list.map(this.mapChannels.bind(this))
				: formValue.channels.map(this.mapChannels.bind(this));

		if (this.customFormIds?.length) {
			senderIds.forEach((senderId) => {
				senderId.customFormIds = this.customFormIds;
				senderId.customFields = this.customForm.value;
			});
		}

		this.allowedDeactivation.next(false);
		this.loading$.next(true);
		this.sendersService
			.requestSenderId(senderIds)
			.pipe(
				finalize(() => this.loading$.next(false)),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((result) => {
				this.allowedDeactivation.next(true);
				if (result.Success) {
					this.editPanel.close(true);
				}
			});
	}

	private mapChannels(channel: Channel): SenderSubscription {
		const sender: SenderSubscription = {
			id: null,
			...this.senderIdForm.value,
			name: this.senderIdForm.controls.name.value.trim(),
			trafficType: channel.channelType,
			enabled: SenderSubscriptionEnabledStatus.DISABLED,
			subscribers: [],
			activeFrom: null,
			activeTill: null,
			whatsappSetting:
				channel.channelType === TrafficType.WHATSAPP ? this.whatsappSettingsForm.getRawValue() : undefined,
		};
		delete sender.channels;
		return sender;
	}
}
